import React, { useMemo, useState } from 'react';
import {
  FormControl,
  FormLabel,
  HStack,
  Select,
  Button,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import StudentPicker from 'views/Dashboard/StudentStatusAdmin/components/StudentPicker';

// Agrupa un array de group_names "crudos" en cursos normalizados para mostrar
// (varios valores crudos pueden representar el mismo curso real). Duplicado
// intencional del helper homónimo en PedagogicalExitsTab.js/
// NewPedagogicalExitModal.js — ambos lo usan también para renderizar su
// propio listado, no solo el form, así que se queda ahí.
export const groupRawNames = (rawGroups, courseOptions) => {
  const map = {};
  for (const raw of rawGroups ?? []) {
    const code = normalizeCourseName(raw);
    const known = courseOptions.find((o) => o.code === code);
    const label = known?.label ?? codeToDisplayLabel(code) ?? raw;
    if (!map[code]) map[code] = { code, label, rawValues: [] };
    map[code].rawValues.push(raw);
  }
  return Object.values(map);
};

/**
 * Campos "a quién cubre la salida": cursos completos (group_names) +
 * alumnos individuales sueltos (student_ids), sin importar su curso.
 * Compartido entre PedagogicalExitsTab.js (tab Accesos QR) y
 * NewPedagogicalExitModal.js (quick action del dashboard) para no
 * mantener dos copias del mismo selector.
 */
const ExitTargetsFields = ({
  groupNames,
  onGroupNamesChange,
  students,
  onStudentsChange,
  courseOptions,
}) => {
  const [courseToAdd, setCourseToAdd] = useState('');

  const selectedCourses = useMemo(
    () => groupRawNames(groupNames, courseOptions),
    [groupNames, courseOptions]
  );
  const availableCourseOptions = useMemo(
    () => courseOptions.filter((o) => !selectedCourses.some((s) => s.code === o.code)),
    [courseOptions, selectedCourses]
  );

  const addCourse = () => {
    if (!courseToAdd) return;
    const option = courseOptions.find((o) => o.code === courseToAdd);
    if (!option) return;
    const newRaws = option.rawValues.filter((r) => !groupNames.includes(r));
    if (newRaws.length === 0) return;
    onGroupNamesChange([...groupNames, ...newRaws]);
    setCourseToAdd('');
  };

  const removeCourse = (code) => {
    const course = selectedCourses.find((c) => c.code === code);
    if (!course) return;
    onGroupNamesChange(groupNames.filter((g) => !course.rawValues.includes(g)));
  };

  const addStudent = (picked) => {
    if (!picked?.id) return;
    if (students.some((s) => s.id === picked.id)) return;
    onStudentsChange([
      ...students,
      { id: picked.id, name: picked.name, username: picked.username, group: picked.group },
    ]);
  };

  const removeStudent = (id) => onStudentsChange(students.filter((s) => s.id !== id));

  return (
    <>
      <FormControl>
        <FormLabel fontSize="sm">Cursos / grupos</FormLabel>
        <HStack>
          <Select
            size="sm"
            borderRadius="8px"
            placeholder="Selecciona un curso…"
            value={courseToAdd}
            onChange={(e) => setCourseToAdd(e.target.value)}
            isDisabled={availableCourseOptions.length === 0}
          >
            {availableCourseOptions.map((o) => (
              <option key={o.code} value={o.code}>{o.label}</option>
            ))}
          </Select>
          <Button size="sm" onClick={addCourse} isDisabled={!courseToAdd}>Agregar</Button>
        </HStack>
        {selectedCourses.length > 0 && (
          <Wrap spacing={2} mt={2}>
            {selectedCourses.map((c) => (
              <WrapItem key={c.code}>
                <Tag size="sm" borderRadius="full" colorScheme="blue">
                  <TagLabel>{c.label}</TagLabel>
                  <TagCloseButton onClick={() => removeCourse(c.code)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Alumnos individuales (opcional)</FormLabel>
        <StudentPicker value={null} onSelect={addStudent} placeholder="Buscar alumno por nombre o RUT…" />
        {students.length > 0 && (
          <Wrap spacing={2} mt={2}>
            {students.map((s) => (
              <WrapItem key={s.id}>
                <Tag size="sm" borderRadius="full" colorScheme="purple">
                  <TagLabel>{s.name}{s.group ? ` · ${s.group}` : ''}</TagLabel>
                  <TagCloseButton onClick={() => removeStudent(s.id)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </FormControl>
    </>
  );
};

export default ExitTargetsFields;
